
import React, { useState } from "react";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import LocationCard from "@/components/scouting/LocationCard";
import LocationDialog from "@/components/scouting/LocationDialog";
import ScoutingRunCard from "@/components/scouting/ScoutingRunCard";
import ScoutingRunDialog from "@/components/scouting/ScoutingRunDialog";

// Note: This page uses mock data and needs to be updated for Firebase integration
// Placeholder interfaces and functions
interface Location { id: string; growerId: string; farmId: string; fieldId: string; defaultAssigneeId: string; }
interface ScoutingRun { id: string; name: string; description: string; startDate: string; endDate: string; locationIds: string[]; traitIds: string[]; assigneeIds: string[]; status: "planned" | "in-progress" | "completed";
crop?: string; }
const locations: Location[] = [];
const scoutingRuns: ScoutingRun[] = [];
const addLocation = (data: any) => ({ id: 'temp', ...data });
const updateLocation = (id: string, data: any) => true;
const deleteLocation = (id: string) => true;
const addScoutingRun = (data: any) => ({ id: 'temp', ...data });
const updateScoutingRun = (id: string, data: any) => true;
const deleteScoutingRun = (id: string) => true;

const Scouting: React.FC = () => {
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState("scouting-runs"); // Changed default to scouting-runs
  
  // Location state
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);
  const [deleteLocationId, setDeleteLocationId] = useState<string | undefined>(undefined);
  
  // Scouting run state
  const [isScoutingRunDialogOpen, setIsScoutingRunDialogOpen] = useState(false);
  const [selectedScoutingRun, setSelectedScoutingRun] = useState<ScoutingRun | undefined>(undefined);
  const [deleteScoutingRunId, setDeleteScoutingRunId] = useState<string | undefined>(undefined);
  const [viewRunDialogOpen, setViewRunDialogOpen] = useState(false);
  const [viewRunId, setViewRunId] = useState<string | undefined>(undefined);
  const renderedDeleteLocationId = useRetainedValue(deleteLocationId);
  const renderedDeleteScoutingRunId = useRetainedValue(deleteScoutingRunId);
  
  // Location handlers
  const handleAddLocation = () => {
    setSelectedLocation(undefined);
    setIsLocationDialogOpen(true);
  };
  
  const handleEditLocation = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setSelectedLocation(location);
      setIsLocationDialogOpen(true);
    }
  };
  
  const handleDeleteLocation = (locationId: string) => {
    setDeleteLocationId(locationId);
  };
  
  const confirmDeleteLocation = () => {
    if (renderedDeleteLocationId) {
      const result = deleteLocation(renderedDeleteLocationId);
      if (result) {
        toast({
          title: "Success",
          description: "Location has been deleted",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete location",
          variant: "destructive",
        });
      }
      // Don't manually clear deleteLocationId here - let Radix's AlertDialogAction
      // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    }
  };
  
  const handleSaveLocation = (locationData: Omit<Location, "id">) => {
    if (selectedLocation) {
      // Update location
      const updated = updateLocation(selectedLocation.id, locationData);
      if (updated) {
        toast({
          title: "Success",
          description: "Location has been updated",
        });
      }
    } else {
      // Add new location
      const newLocation = addLocation(locationData);
      toast({
        title: "Success",
        description: "New location has been added",
      });
    }
  };
  
  // Scouting run handlers
  const handleAddScoutingRun = () => {
    setSelectedScoutingRun(undefined);
    setIsScoutingRunDialogOpen(true);
  };
  
  const handleEditScoutingRun = (scoutingRunId: string) => {
    const scoutingRun = scoutingRuns.find(run => run.id === scoutingRunId);
    if (scoutingRun) {
      setSelectedScoutingRun(scoutingRun);
      setIsScoutingRunDialogOpen(true);
    }
  };
  
  const handleDeleteScoutingRun = (scoutingRunId: string) => {
    setDeleteScoutingRunId(scoutingRunId);
  };
  
  const confirmDeleteScoutingRun = () => {
    if (renderedDeleteScoutingRunId) {
      const result = deleteScoutingRun(renderedDeleteScoutingRunId);
      if (result) {
        toast({
          title: "Success",
          description: "Scouting run has been deleted",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete scouting run",
          variant: "destructive",
        });
      }
      // Don't manually clear deleteScoutingRunId here - let Radix's AlertDialogAction
      // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    }
  };
  
  const handleSaveScoutingRun = (scoutingRunData: Omit<ScoutingRun, "id">) => {
    if (selectedScoutingRun) {
      // Update scouting run
      const updated = updateScoutingRun(selectedScoutingRun.id, scoutingRunData);
      if (updated) {
        toast({
          title: "Success",
          description: "Scouting run has been updated",
        });
      }
    } else {
      // Add new scouting run
      const newRun = addScoutingRun(scoutingRunData);
      toast({
        title: "Success",
        description: "New scouting run has been added",
      });
    }
  };
  
  const handleViewScoutingRun = (scoutingRunId: string) => {
    setViewRunId(scoutingRunId);
    setViewRunDialogOpen(true);
  };
  
  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Scouting</h1>
      </div>
      
      <Tabs defaultValue="scouting-runs" value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center mb-4">
          <TabsList>
            {/* Swapped the order of the tabs */}
            <TabsTrigger value="scouting-runs">Scouting Runs</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
          </TabsList>
          
          {activeTab === "locations" ? (
            <Button onClick={handleAddLocation}>
              <Plus className="mr-2 h-4 w-4" /> Add Location
            </Button>
          ) : (
            <Button onClick={handleAddScoutingRun}>
              <Plus className="mr-2 h-4 w-4" /> Add Scouting Run
            </Button>
          )}
        </div>
        
        <TabsContent value="locations" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map(location => (
              <LocationCard
                key={location.id}
                location={location}
                onEdit={handleEditLocation}
                onDelete={handleDeleteLocation}
              />
            ))}
            {locations.length === 0 && (
              <div className="col-span-1 md:col-span-2 lg:col-span-3 p-8 text-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">No locations added yet. Click "Add Location" to get started.</p>
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="scouting-runs" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {scoutingRuns.map(scoutingRun => (
              <ScoutingRunCard
                key={scoutingRun.id}
                scoutingRun={scoutingRun}
                onEdit={handleEditScoutingRun}
                onDelete={handleDeleteScoutingRun}
                onView={handleViewScoutingRun}
              />
            ))}
            {scoutingRuns.length === 0 && (
              <div className="col-span-1 lg:col-span-2 p-8 text-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">No scouting runs added yet. Click "Add Scouting Run" to get started.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Location Dialog */}
      <LocationDialog
        open={isLocationDialogOpen}
        onOpenChange={setIsLocationDialogOpen}
        location={selectedLocation}
        onSave={handleSaveLocation}
      />
      
      {/* Scouting Run Dialog */}
      <ScoutingRunDialog
        open={isScoutingRunDialogOpen}
        onOpenChange={setIsScoutingRunDialogOpen}
        scoutingRun={selectedScoutingRun}
        onSave={handleSaveScoutingRun}
      />
      
      {/* Delete Location Dialog */}
      <AlertDialog open={!!deleteLocationId} onOpenChange={() => setDeleteLocationId(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the location.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteLocation} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Scouting Run Dialog */}
      <AlertDialog open={!!deleteScoutingRunId} onOpenChange={() => setDeleteScoutingRunId(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scouting Run?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the scouting run and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteScoutingRun} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* View Scouting Run Dialog */}
      <AlertDialog open={viewRunDialogOpen} onOpenChange={setViewRunDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Scouting Run Details</AlertDialogTitle>
            <AlertDialogDescription>
              This feature is not implemented yet. The detailed view of scouting runs will be available in a future update.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setViewRunDialogOpen(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Scouting;
